<script setup lang="ts">
import { localize } from "@/utils";
import TabMenu from "@/components/TabMenu.vue";
import { IBaseMenuItem } from "@/models";

const props = defineProps({
  menuItems: { type: Array as () => IBaseMenuItem[], default: [] },
});
</script>

<template>
  <ul class="nav nav-tabs-fluid">
    <tab-menu :items="menuItems" />
  </ul>
  <div class="content">
    <div class="content-hd">
      <h3 class="fs-2">서비스 프로필 설정</h3>
      <p class="info-txt">프로필 정보의 일부는 서비스 정보에 표기됩니다.</p>
    </div>

    <!-- 프로필 사진 -->
    <div class="fieldset wide">
      <h4 class="fs-3">프로필 사진</h4>
      <div class="flex-fill">
        <div class="form-group row">
          <div class="form-label col-2">로고</div>
          <div class="col">
            <div class="profile-file">
              <div class="profile-thumb"></div>
              <div>
                <p class="fs-sm text-muted mb-3">
                  플래너 로고 이미지를 권장하며,<br />
                  용량 5MB 이하의 jpg, png 파일을 등록해주세요.
                </p>
                <div class="profile-btn">
                  <label class="btn btn-sm"> <input type="file" class="sr-only" />등록하기 </label>
                  <a href="" class="btn btn-outline btn-sm">삭제하기</a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="form-group row">
          <div class="form-label col-2">목록 대표 이미지</div>
          <div class="col">
            <div class="thumbnail-file-wrap">
              <div class="thumbnail-file">
                <label class="thumbnail-file-label"><input type="file" class="sr-only" /></label>
                <img src="@/assets/playground/img/_temp/img1.jpg" alt="" />
                <button type="button" class="icon icon-x">삭제</button>
              </div>
              <div class="thumbnail-file">
                <label class="thumbnail-file-label"><input type="file" class="sr-only" /></label>
              </div>
            </div>
            <p class="form-text">
              플래너 리스트에서 보여줄 이미지로, 최소 312px *312px 정사각형 비율의 png, jpg 파일을 등록해주세요.
            </p>
          </div>
        </div>
        <div class="form-group row">
          <div class="form-label col-2">상세 대표 이미지</div>
          <div class="col">
            <div class="thumbnail-file-wrap">
              <div class="thumbnail-file">
                <label class="thumbnail-file-label wide"><input type="file" class="sr-only" /></label>
                <img src="@/assets/playground/img/_temp/img2.jpg" alt="" />
                <button type="button" class="icon icon-x">삭제</button>
              </div>
              <div class="thumbnail-file">
                <label class="thumbnail-file-label"><input type="file" class="sr-only" /></label>
              </div>
            </div>
            <p class="form-text">
              플래너 상세에서 보여줄 대표 이미지로, 가로 1320px 세로 280px 이상의 이미지를 등록해주세요.
            </p>
          </div>
        </div>
        <div class="form-group row">
          <div class="form-label col-2">서브 이미지</div>
          <div class="col">
            <div class="thumbnail-file-wrap">
              <div class="thumbnail-file">
                <label class="thumbnail-file-label"><input type="file" class="sr-only" /></label>
              </div>
              <div class="thumbnail-file">
                <label class="thumbnail-file-label"><input type="file" class="sr-only" /></label>
              </div>
              <div class="thumbnail-file">
                <label class="thumbnail-file-label"><input type="file" class="sr-only" /></label>
              </div>
            </div>
            <p class="form-text">
              플래너 대표 이미지의 아래에 보여줄 이미지로, 최소 320px *320px 정사각형 비율의 png, jpg 파일을
              등록해주세요.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- 프로필 정보 -->
    <div class="fieldset wide">
      <h4 class="fs-3">프로필 정보</h4>
      <div class="flex-fill">
        <div class="form-group row">
          <div class="form-label form-label-plain col-1">별칭</div>
          <div class="col">
            <div class="input-group">
              <input type="text" class="form-control w2x" />
              <button type="submit" class="btn btn-sm">중복 확인</button>
            </div>
          </div>
        </div>
        <div class="form-group row">
          <div class="form-label col-1">소개</div>
          <div class="col">
            <textarea class="form-control" rows="8" placeholder="자신을 소개하는 메세지를 남겨주세요."></textarea>
          </div>
        </div>
        <div class="form-group row">
          <div class="form-label form-label-plain col-1">대응언어</div>
          <div class="col">
            <select class="form-control form-select">
              <option value="">언어선택</option>
            </select>
            <div class="tag-wrap">
              <span class="tag">한국어 <i class="icon icon-close-org" role="button">삭제</i></span>
              <span class="tag">영어 <i class="icon icon-close-org" role="button">삭제</i></span>
              <span class="tag">일본어 <i class="icon icon-close-org" role="button">삭제</i></span>
              <span class="tag">중국어 <i class="icon icon-close-org" role="button">삭제</i></span>
            </div>
          </div>
        </div>
        <div class="form-group row">
          <div class="form-label col-1">연락처</div>
          <div class="col">
            <div class="form-row justify-content-start">
              <div class="col-4">
                <div class="form-label fs-sm mb-2">핸드폰 번호</div>
                <input type="tel" class="form-control" value="82-10-7322-2455" disabled />
              </div>
              <div class="col-4">
                <div class="form-label fs-sm mb-2">이메일 주소</div>
                <input type="email" class="form-control" />
              </div>
            </div>
          </div>
        </div>
        <div class="form-group row">
          <div class="form-label form-label-plain col-1">주소</div>
          <div class="col">
            <div class="input-group">
              <select class="form-control form-select">
                <option value="">대한민국</option>
              </select>
              <input type="text" class="form-control w3x" />
            </div>
          </div>
        </div>
        <div class="form-group row">
          <div class="form-label col-1">동기화</div>
          <div class="col">
            <label class="form-switch">
              <input class="form-switch-input" type="checkbox" />
              <span class="form-switch-label">Player 프로필 정보의 연락처와 주소 동기화 하기</span>
            </label>
            <p class="form-text">
              프로필 정보와 별도로 작업을 위한 공간을 따로 구비해두었다면, 개인 정보가 아닌 이 작업 공간에 대한 정보를
              플래너의 영업 정보로 입력하실 수도 있습니다. 회원 정보의 연락처와 주소 정보를 동기화하면 [플래너 회원의
              개인 정보 변경 시] 연락처와 주소와 정보가 플래너의 영업 정보로 자동으로 동기화 됩니다.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
